import { motion } from "framer-motion";
import { ShieldCheck, Award, PiggyBank, Zap, CalendarDays, ArrowRight, FileText, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import facilityWhatImg from "@/assets/services/qualification-validation/offerings/facility-what.png";
import facilityCleanroomImg from "@/assets/services/qualification-validation/offerings/facility-cleanroom.jpg";
import facilityProcessImg from "@/assets/services/qualification-validation/offerings/facility-process.jpg";
import { useLanguage, type SiteLang } from "@/hooks/useLanguage";

interface Props {
  onCtaClick: () => void;
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

type Copy = {
  whatTitle: string;
  whatP1: string; // may contain <strong>
  whatP2: string;
  whatAlt: string;
  whyTitle: string;
  whyIntro: string;
  benefits: { title: string; text: string }[];
  cleanTitle: string;
  cleanP1: string;
  cleanP2: string;
  cleanP3: string;
  cleanAlt: string;
  processTitle: string;
  processIntro: string;
  processSteps: { title: string; text: string }[];
  processAlt: string;
  partnerTitle: string;
  partnerIntro: string;
  bullets: string[];
  contactCta: string;
};

const COPY: Record<SiteLang, Copy> = {
  en: {
    whatTitle: "What is facility qualification?",
    whatP1: 'Facility qualification is the process of confirming that physical spaces, such as <strong class="text-foreground">cleanrooms, warehouses, and HVAC (heating, ventilation, and air conditioning) systems</strong>, meet predetermined specifications and regulatory requirements.',
    whatP2: "This process involves comprehensive testing and documentation to ensure these facilities are designed, constructed, and maintained to operate reliably and efficiently within the required standards.",
    whatAlt: "Qualifying a New GMP Facility — QbD Group",
    whyTitle: "Why is facility qualification important?",
    whyIntro: "Facility qualification is more than a regulatory requirement; it is essential for ensuring operational integrity and product quality. Here's why it matters:",
    benefits: [
      { title: "Regulatory compliance", text: "Validating facilities and utilities ensures compliance with regulatory standards, safeguarding against disruptions to operations." },
      { title: "Enabling quality products", text: "Validated facilities and utilities contribute to the production of high-quality products by providing the necessary infrastructure and environmental conditions." },
      { title: "Cost savings", text: "It helps identify and mitigate risks associated with facility and utility failures, reducing the likelihood of downtime and safety incidents." },
      { title: "Operational efficiency", text: "Ensuring the reliability and efficiency of facilities and utilities optimizes operational performance and reduces resource waste." },
    ],
    cleanTitle: "Cleanroom qualification",
    cleanP1: 'A cleanroom is a <strong class="text-foreground">controlled environment where the concentration of airborne particles is managed and classified</strong>. It is designed, constructed, and operated in a way that effectively controls the introduction, generation, and retention of particles within the room. Additionally, other important parameters such as temperature, humidity, and pressure are controlled as required.',
    cleanP2: 'Cleanrooms are a <strong class="text-foreground">critical component of pharmaceutical manufacturing</strong>, where even the smallest contamination can jeopardize product quality and safety. Whether you\'re producing sterile medications, biologics, or any other pharmaceutical products, maintaining a controlled, contaminant-free environment is paramount to ensure the integrity of your products.',
    cleanP3: 'At QbD Group, <strong class="text-foreground">we specialize in providing comprehensive cleanroom qualification services designed to meet the highest standards of the pharmaceutical industry</strong>. With extensive expertise in cleanroom environments, our team ensures that your facilities operate at peak efficiency, safety, and compliance.',
    cleanAlt: "Facility Qualification — Cleanroom — QbD Group",
    processTitle: "Our process",
    processIntro: 'QbD Group provides <strong class="text-foreground">flexible and scalable facility qualification services</strong> tailored to meet your project requirements. Whether you need a turnkey solution—where QbD manages the entire project from planning and execution to documentation—or a hybrid approach, where your company oversees project management while QbD handles the qualification process, we offer the expertise and adaptability to support your needs.',
    processSteps: [
      { title: "Step 1: Planning", text: "We start with a detailed consultation to define project objectives, regulatory requirements, and timelines. A comprehensive qualification plan is developed to ensure expectations are clear and aligned." },
      { title: "Step 2: Execution", text: "Our team conducts site assessments, testing, and inspections of facilities and utilities. We verify compliance with industry standards through rigorous testing and measurements." },
      { title: "Step 3: Documentation", text: "All qualification activities are documented in detailed reports. These include protocols, testing results, and certifications to demonstrate compliance." },
      { title: "Step 4: Final review and validation", text: "We review all findings, address any gaps, and finalize the qualification process. If needed, corrective actions are proposed to ensure facilities meet the required standards." },
    ],
    processAlt: "Facility Qualification process — QbD Group",
    partnerTitle: "Why partner with QbD Group?",
    partnerIntro: "Facility qualification is a critical step in ensuring your facilities and utilities meet regulatory standards and operate efficiently. Our expert support during this phase helps you achieve compliance, reduce risks, and optimize performance, ultimately contributing to the production of high-quality products and significant cost savings.",
    bullets: [
      "During the C&Q portion of your project, clear and pragmatic project direction and execution are the first steps to a well-executed project.",
      "Our expert support enables you to reduce the overall cost of your project.",
      "Regulatory compliance: validating facilities and utilities ensures compliance with regulatory standards, safeguarding against disruptions to operations.",
      "Enabling quality products: validated facilities and utilities contribute to the production of high-quality products by providing the necessary infrastructure and environmental conditions.",
      "Cost savings: it helps identify and mitigate risks associated with facility and utility failures, reducing the likelihood of downtime and safety incidents.",
      "Operational efficiency: ensuring the reliability and efficiency of facilities and utilities optimizes operational performance and reduces resource waste.",
    ],
    contactCta: "Contact us",
  },
  es: {
    // Spanish not used on this page yet; reuse English placeholders to keep build safe.
    whatTitle: "What is facility qualification?",
    whatP1: 'Facility qualification is the process of confirming that physical spaces, such as <strong class="text-foreground">cleanrooms, warehouses, and HVAC (heating, ventilation, and air conditioning) systems</strong>, meet predetermined specifications and regulatory requirements.',
    whatP2: "This process involves comprehensive testing and documentation to ensure these facilities are designed, constructed, and maintained to operate reliably and efficiently within the required standards.",
    whatAlt: "Qualifying a New GMP Facility — QbD Group",
    whyTitle: "Why is facility qualification important?",
    whyIntro: "Facility qualification is more than a regulatory requirement; it is essential for ensuring operational integrity and product quality. Here's why it matters:",
    benefits: [
      { title: "Regulatory compliance", text: "Validating facilities and utilities ensures compliance with regulatory standards, safeguarding against disruptions to operations." },
      { title: "Enabling quality products", text: "Validated facilities and utilities contribute to the production of high-quality products by providing the necessary infrastructure and environmental conditions." },
      { title: "Cost savings", text: "It helps identify and mitigate risks associated with facility and utility failures, reducing the likelihood of downtime and safety incidents." },
      { title: "Operational efficiency", text: "Ensuring the reliability and efficiency of facilities and utilities optimizes operational performance and reduces resource waste." },
    ],
    cleanTitle: "Cleanroom qualification",
    cleanP1: 'A cleanroom is a <strong class="text-foreground">controlled environment where the concentration of airborne particles is managed and classified</strong>.',
    cleanP2: 'Cleanrooms are a <strong class="text-foreground">critical component of pharmaceutical manufacturing</strong>.',
    cleanP3: 'At QbD Group, <strong class="text-foreground">we specialize in providing comprehensive cleanroom qualification services</strong>.',
    cleanAlt: "Facility Qualification — Cleanroom — QbD Group",
    processTitle: "Our process",
    processIntro: 'QbD Group provides <strong class="text-foreground">flexible and scalable facility qualification services</strong> tailored to meet your project requirements.',
    processSteps: [
      { title: "Step 1: Planning", text: "We start with a detailed consultation to define project objectives." },
      { title: "Step 2: Execution", text: "Our team conducts site assessments, testing, and inspections." },
      { title: "Step 3: Documentation", text: "All qualification activities are documented in detailed reports." },
      { title: "Step 4: Final review and validation", text: "We review all findings and finalize the qualification process." },
    ],
    processAlt: "Facility Qualification process — QbD Group",
    partnerTitle: "Why partner with QbD Group?",
    partnerIntro: "Facility qualification is a critical step in ensuring your facilities and utilities meet regulatory standards and operate efficiently.",
    bullets: [],
    contactCta: "Contact us",
  },
  "zh-cn": {
    whatTitle: "什么是设施确认？",
    whatP1: '设施确认是确认物理空间——例如<strong class="text-foreground">洁净室、仓库以及 HVAC（采暖、通风与空调）系统</strong>——符合预先设定的规范和法规要求的过程。',
    whatP2: "该过程涉及全面的测试与文档工作，确保这些设施按所需标准进行设计、建造和维护，从而可靠且高效地运行。",
    whatAlt: "Qualifying a New GMP Facility — QbD Group",
    whyTitle: "为什么设施确认很重要？",
    whyIntro: "设施确认不仅是一项法规要求，更是保障运营完整性和产品质量的关键。原因如下：",
    benefits: [
      { title: "法规合规", text: "对设施和 utilities 进行验证可确保符合法规标准，避免运营中断。" },
      { title: "支持优质产品", text: "经过验证的设施和 utilities 提供必要的基础设施和环境条件，从而支持高质量产品的生产。" },
      { title: "节省成本", text: "有助于识别并降低设施和 utilities 故障相关的风险，减少停机和安全事故的可能性。" },
      { title: "运营效率", text: "保障设施和 utilities 的可靠性与效率，优化运营表现并减少资源浪费。" },
    ],
    cleanTitle: "洁净室确认",
    cleanP1: '洁净室是一种<strong class="text-foreground">对空气中悬浮粒子浓度进行管理和分级的受控环境</strong>。其设计、建造与运行方式能够有效控制粒子在室内的引入、产生与滞留。同时，温度、湿度和压力等其他重要参数也会根据需要加以控制。',
    cleanP2: '洁净室是<strong class="text-foreground">药品生产中的关键环节</strong>，哪怕最微小的污染都可能危及产品质量与安全。无论您生产无菌药品、生物制品还是其他药品，维持受控且无污染的环境对于确保产品完整性都至关重要。',
    cleanP3: '在 QbD Group，<strong class="text-foreground">我们专注于提供符合制药行业最高标准的全面洁净室确认服务</strong>。凭借在洁净室环境方面的丰富经验，我们的团队确保您的设施在效率、安全与合规方面均处于最佳状态。',
    cleanAlt: "Facility Qualification — Cleanroom — QbD Group",
    processTitle: "我们的流程",
    processIntro: 'QbD Group 提供<strong class="text-foreground">灵活且可扩展的设施确认服务</strong>，量身满足您的项目需求。无论您需要由 QbD 负责从规划、执行到文档全流程的交钥匙解决方案，还是希望由贵方负责项目管理、由 QbD 负责确认流程的混合模式，我们都能提供相应的专业能力与灵活性来支持您的需求。',
    processSteps: [
      { title: "第 1 步：规划", text: "我们从详细的咨询开始，明确项目目标、法规要求和时间计划。制定全面的确认计划，确保各方期望清晰一致。" },
      { title: "第 2 步：执行", text: "我们的团队对设施和 utilities 进行现场评估、测试与检查，通过严格的测试和测量验证其是否符合行业标准。" },
      { title: "第 3 步：文档", text: "所有确认活动都会记录在详细报告中，包含协议、测试结果及合规证明文件。" },
      { title: "第 4 步：最终审查与验证", text: "我们审查所有结果、解决发现的差距并完成确认流程。如有需要，将提出纠正措施，确保设施达到所需标准。" },
    ],
    processAlt: "Facility Qualification process — QbD Group",
    partnerTitle: "为什么与 QbD Group 合作？",
    partnerIntro: "设施确认是确保您的设施和 utilities 符合法规标准并高效运行的关键步骤。我们在此阶段的专业支持可帮助您实现合规、降低风险、优化性能，最终助力高质量产品的生产并显著节省成本。",
    bullets: [
      "在项目的 C&Q 阶段，清晰且务实的项目方向与执行是项目成功的第一步。",
      "我们的专业支持能够帮助您降低整体项目成本。",
      "法规合规：对设施和 utilities 进行验证可确保符合法规标准，避免运营中断。",
      "支持优质产品：经过验证的设施和 utilities 提供必要的基础设施和环境条件，从而支持高质量产品的生产。",
      "节省成本：有助于识别并降低设施和 utilities 故障相关的风险，减少停机和安全事故的可能性。",
      "运营效率：保障设施和 utilities 的可靠性与效率，优化运营表现并减少资源浪费。",
    ],
    contactCta: "联系我们",
  },
};

const FacilityQualificationContent = ({ onCtaClick }: Props) => {
  const lang = useLanguage();
  const t = COPY[lang] ?? COPY.en;

  return (
    <div className="bg-background">
      {/* ── What is facility qualification? ── */}
      <section className="py-20 sm:py-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <motion.h2
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={0}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-8"
          >
            {t.whatTitle}
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-5">
              <motion.p
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={1}
                className="text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t.whatP1 }}
              />
              <motion.p
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={2}
                className="text-muted-foreground leading-relaxed"
              >
                {t.whatP2}
              </motion.p>
            </div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp} custom={2}
              className="rounded-2xl overflow-hidden"
            >
              <img
                src={facilityWhatImg}
                alt={t.whatAlt}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Why is facility qualification important? ── */}
      <section className="py-20 sm:py-28 bg-secondary">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <motion.h2
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={0}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-4"
          >
            {t.whyTitle}
          </motion.h2>

          <motion.p
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={1}
            className="text-muted-foreground leading-relaxed mb-12 max-w-3xl"
          >
            {t.whyIntro}
          </motion.p>

          <div className="grid sm:grid-cols-2 gap-8">
            {t.benefits.map((b, i) => {
              const Icon = [ShieldCheck, Award, PiggyBank, Zap][i] ?? ShieldCheck;
              return (
                <motion.div
                  key={b.title}
                  initial="hidden" whileInView="visible" viewport={{ once: true }}
                  variants={fadeUp} custom={i}
                  className="flex items-start gap-4"
                >
                  <Icon className="w-6 h-6 shrink-0 mt-1" style={{ color: "var(--service-color)" }} />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{b.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{b.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Cleanroom qualification ── */}
      <section className="py-20 sm:py-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <motion.h2
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={0}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-6"
          >
            {t.cleanTitle}
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-5">
              <motion.p
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={1}
                className="text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t.cleanP1 }}
              />
              <motion.p
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={2}
                className="text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t.cleanP2 }}
              />
              <motion.p
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={3}
                className="text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t.cleanP3 }}
              />
            </div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp} custom={2}
              className="rounded-2xl overflow-hidden"
            >
              <img
                src={facilityCleanroomImg}
                alt={t.cleanAlt}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Our process ── */}
      <section className="py-20 sm:py-28 bg-secondary">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <motion.h2
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={0}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-6"
          >
            {t.processTitle}
          </motion.h2>

          <motion.p
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={1}
            className="text-muted-foreground leading-relaxed mb-12 max-w-3xl"
            dangerouslySetInnerHTML={{ __html: t.processIntro }}
          />

          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              {t.processSteps.map((step, i) => {
                const Icon = [CalendarDays, ArrowRight, FileText, CheckCircle][i] ?? CalendarDays;
                return (
                  <motion.div
                    key={step.title}
                    initial="hidden" whileInView="visible" viewport={{ once: true }}
                    variants={fadeUp} custom={i}
                    className="flex items-start gap-4"
                  >
                    <Icon className="w-6 h-6 shrink-0 mt-1" style={{ color: "var(--service-color)" }} />
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{step.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp} custom={2}
              className="rounded-2xl overflow-hidden"
            >
              <img
                src={facilityProcessImg}
                alt={t.processAlt}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Why partner with QbD Group? ── */}
      <section className="py-20 sm:py-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <motion.h2
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={0}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-6"
          >
            {t.partnerTitle}
          </motion.h2>

          <motion.p
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={1}
            className="text-muted-foreground leading-relaxed mb-8 max-w-3xl"
          >
            {t.partnerIntro}
          </motion.p>

          <ul className="space-y-4 mb-10 max-w-3xl">
            {t.bullets.map((bullet, i) => (
              <motion.li
                key={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i + 2}
                className="flex items-start gap-3 text-muted-foreground leading-relaxed"
              >
                <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--service-color)" }} />
                <span>{bullet}</span>
              </motion.li>
            ))}
          </ul>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={8}
          >
            <Button onClick={onCtaClick} className="rounded-full">
              {t.contactCta}
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FacilityQualificationContent;
